import { useState, useCallback, useEffect } from 'react';
import { FREQUENCY_COLORS, FREQUENCY_GLOW_COLORS } from '@/types';
import { hasSeenChapterIntro, markChapterIntroSeen } from '@/utils/tutorialStorage';

// Re-export for convenience
export { hasSeenChapterIntro, markChapterIntroSeen };

function getColor(freq: number): string {
  return FREQUENCY_COLORS[freq] ?? '#888';
}

function getGlow(freq: number): string {
  return FREQUENCY_GLOW_COLORS[freq] ?? '#aaa';
}

// ── Types ──

interface ChapterIntroSlide {
  title: string;
  subtitle: string;
  visual: React.ReactNode;
}

interface ChapterIntroProps {
  chapter: number;
  onComplete: () => void;
}

// ── Chapter-specific visuals ──

function IntervalsVisual() {
  return (
    <div style={visualContainerStyle}>
      <div style={{
        position: 'absolute',
        top: '35%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: '0',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
          boxShadow: `0 0 10px ${getColor(1)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          background: 'rgba(81, 207, 102, 0.6)',
          position: 'relative',
        }}>
          <span style={{
            position: 'absolute',
            top: '-20px',
            left: '50%',
            transform: 'translateX(-50%)',
            fontSize: '12px',
            color: 'rgba(199, 125, 255, 0.8)',
            fontFamily: "system-ui, -apple-system, sans-serif",
            fontWeight: 500,
          }}>P5</span>
        </div>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(8)}, ${getColor(8)})`,
          boxShadow: `0 0 10px ${getColor(8)}80`,
        }} />
      </div>
      {/* Interval legend */}
      <div style={{
        position: 'absolute',
        bottom: '15%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        flexDirection: 'column',
        gap: '6px',
        alignItems: 'center',
      }}>
        {[
          { label: 'P5', desc: 'Perfect Fifth', val: '7 semitones' },
          { label: 'M3', desc: 'Major Third', val: '4 semitones' },
          { label: 'm3', desc: 'Minor Third', val: '3 semitones' },
        ].map((item, i) => (
          <div key={i} style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
            fontSize: '11px',
            color: 'rgba(232,232,228,0.5)',
            fontFamily: "system-ui, -apple-system, sans-serif",
          }}>
            <span style={{
              color: 'rgba(199, 125, 255, 0.7)',
              fontWeight: 600,
              width: '24px',
              textAlign: 'right',
            }}>{item.label}</span>
            <span>{item.desc}</span>
            <span style={{ color: 'rgba(232,232,228,0.3)' }}>({item.val})</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function IntervalsVisual2() {
  return (
    <div style={visualContainerStyle}>
      {/* Satisfied - correct difference */}
      <div style={{
        position: 'absolute',
        top: '25%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <div style={{
            ...introNodeStyle,
            background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
            boxShadow: `0 0 10px ${getColor(1)}80`,
          }} />
          <div style={freqLabelStyle}>C</div>
        </div>
        <div style={{
          flex: 1,
          height: '2px',
          background: 'rgba(81, 207, 102, 0.7)',
          boxShadow: '0 0 4px rgba(81, 207, 102, 0.4)',
        }} />
        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <div style={{
            ...introNodeStyle,
            background: `radial-gradient(circle at 40% 35%, ${getGlow(8)}, ${getColor(8)})`,
            boxShadow: `0 0 10px ${getColor(8)}80`,
          }} />
          <div style={freqLabelStyle}>G</div>
        </div>
      </div>
      <div style={{
        position: 'absolute',
        top: '43%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: 'rgba(81, 207, 102, 0.7)',
        fontStyle: 'italic',
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>difference = 7 semitones (P5)</div>

      {/* Unsatisfied - wrong difference */}
      <div style={{
        position: 'absolute',
        top: '60%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <div style={{
            ...introNodeStyle,
            background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
            opacity: 0.6,
          }} />
          <div style={freqLabelStyle}>C</div>
        </div>
        <div style={{
          flex: 1,
          height: '2px',
          background: 'repeating-linear-gradient(90deg, rgba(232,232,228,0.2) 0px, rgba(232,232,228,0.2) 6px, transparent 6px, transparent 12px)',
        }} />
        <div style={{ textAlign: 'center', flex: '0 0 auto' }}>
          <div style={{
            ...introNodeStyle,
            background: `radial-gradient(circle at 40% 35%, ${getGlow(3)}, ${getColor(3)})`,
            opacity: 0.6,
          }} />
          <div style={freqLabelStyle}>D</div>
        </div>
      </div>
      <div style={{
        position: 'absolute',
        top: '78%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '10px',
        color: 'rgba(232,232,228,0.35)',
        fontStyle: 'italic',
        fontFamily: "system-ui, -apple-system, sans-serif",
      }}>difference = 2 semitones (not P5)</div>
    </div>
  );
}

function CurrentVisual() {
  return (
    <div style={visualContainerStyle}>
      {/* Amplifying pathway */}
      <div style={{
        position: 'absolute',
        top: '28%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(5)}, ${getColor(5)})`,
          boxShadow: `0 0 10px ${getColor(5)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '4px',
          background: 'linear-gradient(90deg, rgba(81, 207, 102, 0.8), rgba(252, 191, 73, 0.8))',
          boxShadow: '0 0 8px rgba(252, 191, 73, 0.4)',
          borderRadius: '2px',
        }} />
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(1)}, ${getColor(1)})`,
          boxShadow: `0 0 10px ${getColor(1)}80`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '42%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: 'rgba(252, 191, 73, 0.7)',
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: 'center',
      }}>Amplifying (doubles effect)</div>

      {/* Dampening pathway */}
      <div style={{
        position: 'absolute',
        top: '60%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(6)}, ${getColor(6)})`,
          boxShadow: `0 0 10px ${getColor(6)}60`,
        }} />
        <div style={{
          flex: 1,
          height: '1px',
          background: 'repeating-linear-gradient(90deg, rgba(232,232,228,0.3) 0px, rgba(232,232,228,0.3) 4px, transparent 4px, transparent 8px)',
        }} />
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(10)}, ${getColor(10)})`,
          boxShadow: `0 0 10px ${getColor(10)}60`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '74%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: 'rgba(232,232,228,0.4)',
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: 'center',
      }}>Dampening (halves effect)</div>
    </div>
  );
}

function ReflectionVisual() {
  return (
    <div style={visualContainerStyle}>
      <div style={{
        position: 'absolute',
        top: '40%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(9)}, ${getColor(9)})`,
          boxShadow: `0 0 10px ${getColor(9)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          position: 'relative',
        }}>
          {/* Wavy line using SVG */}
          <svg
            viewBox="0 0 100 20"
            preserveAspectRatio="none"
            style={{ width: '100%', height: '20px', position: 'absolute', top: '-9px' }}
          >
            <path
              d="M0 10 Q12.5 0 25 10 T50 10 T75 10 T100 10"
              stroke="rgba(247, 37, 133, 0.7)"
              strokeWidth="2"
              fill="none"
            />
          </svg>
        </div>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(11)}, ${getColor(11)})`,
          boxShadow: `0 0 10px ${getColor(11)}80`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '58%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: 'rgba(247, 37, 133, 0.7)',
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: 'center',
        lineHeight: 1.6,
      }}>
        Frequencies decrease instead of increase
      </div>
    </div>
  );
}

function ThresholdVisual() {
  return (
    <div style={visualContainerStyle}>
      {/* Filtering pathway */}
      <div style={{
        position: 'absolute',
        top: '28%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(3)}, ${getColor(3)})`,
          boxShadow: `0 0 10px ${getColor(3)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          background: 'rgba(232,232,228,0.3)',
          position: 'relative',
        }}>
          {/* Diamond filter icon */}
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%) rotate(45deg)',
            width: '10px',
            height: '10px',
            background: 'rgba(252, 191, 73, 0.7)',
            border: '1px solid rgba(252, 191, 73, 0.9)',
          }} />
        </div>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(7)}, ${getColor(7)})`,
          boxShadow: `0 0 10px ${getColor(7)}80`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '42%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: 'rgba(252, 191, 73, 0.7)',
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: 'center',
      }}>Filtering (blocks below threshold)</div>

      {/* Directed pathway */}
      <div style={{
        position: 'absolute',
        top: '60%',
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        width: '75%',
      }}>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(5)}, ${getColor(5)})`,
          boxShadow: `0 0 10px ${getColor(5)}80`,
        }} />
        <div style={{
          flex: 1,
          height: '2px',
          background: 'rgba(199, 125, 255, 0.5)',
          position: 'relative',
        }}>
          {/* Arrow indicator */}
          <div style={{
            position: 'absolute',
            right: '-2px',
            top: '50%',
            transform: 'translateY(-50%)',
            width: 0,
            height: 0,
            borderLeft: '8px solid rgba(199, 125, 255, 0.7)',
            borderTop: '5px solid transparent',
            borderBottom: '5px solid transparent',
          }} />
        </div>
        <div style={{
          ...introNodeStyle,
          background: `radial-gradient(circle at 40% 35%, ${getGlow(10)}, ${getColor(10)})`,
          boxShadow: `0 0 10px ${getColor(10)}80`,
        }} />
      </div>
      <div style={{
        position: 'absolute',
        top: '74%',
        left: '50%',
        transform: 'translateX(-50%)',
        fontSize: '11px',
        color: 'rgba(199, 125, 255, 0.6)',
        fontFamily: "system-ui, -apple-system, sans-serif",
        textAlign: 'center',
      }}>Directed (one-way flow)</div>
    </div>
  );
}

function CascadeVisual() {
  // A web of different pathway types
  const nodes = [
    { x: 50, y: 15, freq: 1 },
    { x: 20, y: 45, freq: 5 },
    { x: 80, y: 45, freq: 9 },
    { x: 35, y: 80, freq: 3 },
    { x: 65, y: 80, freq: 7 },
  ];

  return (
    <div style={visualContainerStyle}>
      <svg
        viewBox="0 0 100 100"
        style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
      >
        {/* Various pathway types */}
        <line x1={nodes[0]?.x} y1={nodes[0]?.y} x2={nodes[1]?.x} y2={nodes[1]?.y}
          stroke="rgba(199, 125, 255, 0.4)" strokeWidth="0.8" />
        <line x1={nodes[0]?.x} y1={nodes[0]?.y} x2={nodes[2]?.x} y2={nodes[2]?.y}
          stroke="rgba(252, 191, 73, 0.5)" strokeWidth="1.5" />
        <line x1={nodes[1]?.x} y1={nodes[1]?.y} x2={nodes[3]?.x} y2={nodes[3]?.y}
          stroke="rgba(247, 37, 133, 0.4)" strokeWidth="0.8" strokeDasharray="3 3" />
        <line x1={nodes[2]?.x} y1={nodes[2]?.y} x2={nodes[4]?.x} y2={nodes[4]?.y}
          stroke="rgba(81, 207, 102, 0.5)" strokeWidth="0.8" />
        <line x1={nodes[3]?.x} y1={nodes[3]?.y} x2={nodes[4]?.x} y2={nodes[4]?.y}
          stroke="rgba(232,232,228,0.3)" strokeWidth="0.8" />
      </svg>
      {nodes.map((node, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            left: `${node.x}%`,
            top: `${node.y}%`,
            transform: 'translate(-50%, -50%)',
            width: '20px',
            height: '20px',
            borderRadius: '50%',
            background: `radial-gradient(circle at 40% 35%, ${getGlow(node.freq)}, ${getColor(node.freq)})`,
            boxShadow: `0 0 8px ${getColor(node.freq)}60`,
            animation: 'tutorialPulse 3s ease-in-out infinite',
            animationDelay: `${i * 0.3}s`,
          }}
        />
      ))}
    </div>
  );
}

function SynthesisVisual() {
  return (
    <div style={{
      ...visualContainerStyle,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      {/* Radiating golden star pattern */}
      {[0, 1, 2, 3, 4, 5].map((i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '2px',
            height: '60px',
            background: 'linear-gradient(to bottom, rgba(252, 191, 73, 0.6), transparent)',
            transformOrigin: 'bottom center',
            transform: `rotate(${i * 60}deg)`,
            animation: 'tutorialPulse 2.5s ease-in-out infinite',
            animationDelay: `${i * 0.2}s`,
          }}
        />
      ))}
      <div style={{
        width: '20px',
        height: '20px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(252, 191, 73, 0.9), rgba(114, 9, 183, 0.6))',
        boxShadow: '0 0 20px rgba(252, 191, 73, 0.5), 0 0 40px rgba(114, 9, 183, 0.3)',
        zIndex: 2,
      }} />
    </div>
  );
}

// ── Slide definitions per chapter ──

function getChapterSlides(chapter: number): ChapterIntroSlide[] {
  switch (chapter) {
    case 2:
      return [
        {
          title: 'New: Harmonic Intervals',
          subtitle: 'Pathways now require specific intervals',
          visual: <IntervalsVisual />,
        },
        {
          title: 'Frequency Difference',
          subtitle: 'The gap between nodes must match the interval',
          visual: <IntervalsVisual2 />,
        },
      ];
    case 3:
      return [
        {
          title: 'New: Amplifying & Dampening',
          subtitle: 'Pathway types alter how energy flows',
          visual: <CurrentVisual />,
        },
      ];
    case 4:
      return [
        {
          title: 'New: Inverting Pathways',
          subtitle: 'Energy reverses direction',
          visual: <ReflectionVisual />,
        },
      ];
    case 5:
      return [
        {
          title: 'New: Filtering & Directed',
          subtitle: 'Pathways that block or restrict flow',
          visual: <ThresholdVisual />,
        },
      ];
    case 6:
      return [
        {
          title: 'New: All Pathways',
          subtitle: 'Master all pathway types in complex networks',
          visual: <CascadeVisual />,
        },
      ];
    case 7:
      return [
        {
          title: 'Final Challenge',
          subtitle: 'Every mechanic. Maximum complexity. Achieve Grand Resonance.',
          visual: <SynthesisVisual />,
        },
      ];
    default:
      return [];
  }
}

// ── Main Component ──

export function ChapterIntro({ chapter, onComplete }: ChapterIntroProps) {
  const slides = getChapterSlides(chapter);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  const advance = useCallback(() => {
    if (isTransitioning) return;

    if (currentSlide >= slides.length - 1) {
      markChapterIntroSeen(chapter);
      setVisible(false);
      setTimeout(() => onComplete(), 300);
      return;
    }

    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentSlide((prev) => prev + 1);
      setIsTransitioning(false);
    }, 300);
  }, [currentSlide, slides.length, isTransitioning, onComplete, chapter]);

  const handleSkip = useCallback(() => {
    markChapterIntroSeen(chapter);
    setVisible(false);
    setTimeout(() => onComplete(), 300);
  }, [onComplete, chapter]);

  if (slides.length === 0) return null;

  const slide = slides[currentSlide];
  if (!slide) return null;

  return (
    <div
      style={{
        ...backdropStyle,
        opacity: visible ? 1 : 0,
      }}
      onClick={advance}
      role="dialog"
      aria-label={`Chapter ${chapter} introduction`}
    >
      {/* Skip button */}
      <button
        style={skipButtonStyle}
        onClick={(e) => {
          e.stopPropagation();
          handleSkip();
        }}
        aria-label="Skip introduction"
        onMouseEnter={(e) => {
          e.currentTarget.style.color = 'rgba(232,232,228,0.6)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = 'rgba(232,232,228,0.3)';
        }}
      >
        Skip
      </button>

      {/* Slide content */}
      <div
        style={{
          ...slideContentStyle,
          opacity: isTransitioning ? 0 : 1,
          transform: isTransitioning ? 'translateY(10px)' : 'translateY(0)',
        }}
      >
        <div style={visualAreaStyle}>
          {slide.visual}
        </div>

        <h2 style={titleStyle}>{slide.title}</h2>
        <p style={subtitleStyle}>{slide.subtitle}</p>
      </div>

      {/* Progress dots (only if >1 slide) */}
      {slides.length > 1 && (
        <div style={dotsContainerStyle}>
          {slides.map((_, i) => (
            <div
              key={i}
              style={{
                ...dotStyle,
                background: i === currentSlide
                  ? 'rgba(199, 125, 255, 0.8)'
                  : 'rgba(232,232,228,0.15)',
                boxShadow: i === currentSlide
                  ? '0 0 6px rgba(199, 125, 255, 0.5)'
                  : 'none',
              }}
            />
          ))}
        </div>
      )}

      <div style={tapContinueStyle}>
        tap to continue
      </div>
    </div>
  );
}

// ── Styles ──

const backdropStyle: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  background: 'rgba(5, 5, 16, 0.95)',
  backdropFilter: 'blur(10px)',
  WebkitBackdropFilter: 'blur(10px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 200,
  cursor: 'pointer',
  transition: 'opacity 0.3s ease',
  userSelect: 'none',
};

const skipButtonStyle: React.CSSProperties = {
  position: 'absolute',
  top: '16px',
  right: '20px',
  background: 'none',
  border: 'none',
  color: 'rgba(232,232,228,0.3)',
  fontSize: '13px',
  fontWeight: 400,
  letterSpacing: '0.06em',
  cursor: 'pointer',
  padding: '8px 12px',
  fontFamily: "system-ui, -apple-system, sans-serif",
  transition: 'color 0.2s ease',
  outline: 'none',
  zIndex: 10,
};

const slideContentStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: '20px',
  maxWidth: '340px',
  width: '90%',
  transition: 'opacity 0.3s ease, transform 0.3s ease',
};

const visualAreaStyle: React.CSSProperties = {
  width: '220px',
  height: '220px',
  position: 'relative',
  flexShrink: 0,
};

const visualContainerStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  position: 'relative',
};

const titleStyle: React.CSSProperties = {
  fontSize: '22px',
  fontWeight: 300,
  color: '#e8e8e4',
  letterSpacing: '0.12em',
  textShadow: '0 0 20px rgba(199, 125, 255, 0.5), 0 0 40px rgba(114, 9, 183, 0.3)',
  margin: 0,
  textAlign: 'center',
  fontFamily: "'Cormorant Garamond', 'Palatino', 'Georgia', serif",
};

const subtitleStyle: React.CSSProperties = {
  fontSize: '13px',
  color: 'rgba(232,232,228,0.5)',
  fontStyle: 'italic',
  margin: 0,
  textAlign: 'center',
  lineHeight: 1.5,
  fontFamily: "system-ui, -apple-system, sans-serif",
};

const dotsContainerStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '60px',
  display: 'flex',
  gap: '10px',
};

const dotStyle: React.CSSProperties = {
  width: '8px',
  height: '8px',
  borderRadius: '50%',
  transition: 'all 0.3s ease',
};

const tapContinueStyle: React.CSSProperties = {
  position: 'absolute',
  bottom: '30px',
  fontSize: '12px',
  color: 'rgba(232,232,228,0.2)',
  fontFamily: "system-ui, -apple-system, sans-serif",
  letterSpacing: '0.1em',
  animation: 'tutorialPulse 3s ease-in-out infinite',
};

const introNodeStyle: React.CSSProperties = {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  flexShrink: 0,
};

const freqLabelStyle: React.CSSProperties = {
  fontSize: '9px',
  color: 'rgba(232,232,228,0.5)',
  fontFamily: "system-ui, -apple-system, sans-serif",
  marginTop: '3px',
  textAlign: 'center',
};
